<template>
  <context-menu
    v-bind="$attrs"
    class="evaluation-loading-menu"
    @update:visible="onVisibilityChange($event)"
    @keydown.down="focusNext"
    @keydown.up="focusPrevious"
    @keydown.tab.exact.prevent="focusNext"
    @keydown.tab.shift.prevent="focusPrevious"
    @close="emit('close')"
  >
    <input
      ref="searchbar"
      v-model="searchString"
      type="text"
      class="evaluation-loading-menu__searchbar"
      :placeholder="$t('labels.filterEntries')"
      @click.prevent=""
    />

    <button
      v-for="evaluation of filteredEvaluations"
      :key="evaluation.id"
      type="button"
      class="context-menu__entry"
      style="display: flex"
      @click="emit('open-evaluation', evaluation.id)"
      @contextmenu="openContextMenu($event, evaluation.id)"
    >
      <span v-if="evaluation.chartType">
        <lucide-icon
          :name="charts[evaluation.chartType].icon"
          style="margin-right: 3px; margin-bottom: -2px"
        />
      </span>
      <span>
        <template v-if="evaluation.isDefaultSimulation">
          {{ $t("labels.openSimulation") }}
        </template>
        <template v-else>
          {{ evaluation.title || $t("labels.unnamed") }}
        </template>
      </span>
    </button>

    <hr v-if="filteredEvaluations.length > 0" />

    <template v-if="overviewsVisible">
      <button
        v-for="overview of filteredOverviews"
        :key="overview.id"
        type="button"
        class="context-menu__entry"
        style="display: flex"
        @click="emit('open-overview', overview.id)"
        @contextmenu="openContextMenu($event, overview.id)"
      >
        <evaluation-overview-layout-symbol
          style="margin-top: 0px"
          :overview-id="overview.id"
        />
        <span>{{ overview.title || $t("labels.unnamed") }}</span>
      </button>

      <hr v-if="filteredOverviews.length > 0" />
    </template>

    <button
      ref="buttonNew"
      :key="'new-evaluation'"
      type="button"
      class="context-menu__entry"
      @click="createAndOpenEvaluation()"
    >
      {{ $t("labels.newEvaluation") }}
    </button>

    <!--
      @contextmenu="openContextMenu($event, evaluation.id)"
    -->
  </context-menu>

  <context-menu
    v-if="contextMenuData.evaluationId != null"
    :x="contextMenuData.x"
    :y="contextMenuData.y"
    @close="contextMenuData.evaluationId = null"
  >
    <button
      type="button"
      class="context-menu__entry"
      @click="deleteEvaluation(contextMenuData.evaluationId)"
    >
      {{ $t("actions.delete") }}
    </button>
  </context-menu>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from "vue";

import ContextMenu from "@core/ui/ContextMenu.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import EvaluationOverviewLayoutSymbol from "./EvaluationOverviewLayoutSymbol.vue";

import { charts } from "../charts/charts";
import { useEvaluationStore } from "./evaluations.store";
import { useEvaluationOverviewStore } from "./evaluationOverviews.store";

defineOptions({
  inheritAttrs: false
});

const props = withDefaults(
  defineProps<{
    openEvaluationIds: Set<number>;
    overviewsVisible?: boolean;
    openOverviewIds?: Set<number>;
  }>(),
  {
    overviewsVisible: false,
    openOverviewIds: () => new Set<number>()
  }
);

const emit = defineEmits<{
  (e: "open-evaluation", evaluationId: number): void;
  (e: "open-overview", overviewId: number): void;
  (e: "close"): void;
}>();

const searchString = ref("");
const searchbar = ref<HTMLInputElement>();
const buttonNew = ref<HTMLButtonElement>();

function onVisibilityChange(visible: boolean) {
  if (visible) {
    searchbar.value?.focus();
  } else {
    searchString.value = "";
  }
}

const unopenedEvaluations = computed(() => {
  return useEvaluationStore().orderedItems.filter(
    (item) => !props.openEvaluationIds.has(item.id)
  );
});

const filteredEvaluations = computed(() => {
  return unopenedEvaluations.value.filter((evaluation) =>
    evaluation.title.toLowerCase().includes(searchString.value.toLowerCase())
  );
});

const unopenedOverviews = computed(() => {
  return useEvaluationOverviewStore().orderedItems.filter(
    (item) => !props.openOverviewIds.has(item.id)
  );
});

const filteredOverviews = computed(() => {
  return unopenedOverviews.value.filter((overview) =>
    overview.title.toLowerCase().includes(searchString.value.toLowerCase())
  );
});

async function createAndOpenEvaluation() {
  const evaluation = await useEvaluationStore().create();
  emit("open-evaluation", evaluation.id);
}

async function deleteEvaluation(evaluationId: number | null) {
  if (evaluationId == null) return;
  const evaluation = useEvaluationStore().items[evaluationId];
  if (evaluation == null) return;
  if (evaluation.isDefaultSimulation) return;
  useEvaluationStore().delete({ id: evaluation?.id });
}

function focusPrevious() {
  const activeElement = document.activeElement;
  if (activeElement == null) {
    buttonNew.value?.focus();
  } else {
    let previousElement = activeElement.previousElementSibling;
    while (previousElement != null) {
      if (
        previousElement instanceof HTMLButtonElement ||
        previousElement instanceof HTMLInputElement
      ) {
        return previousElement.focus();
      } else {
        previousElement = previousElement.previousElementSibling;
      }
    }

    if (previousElement == null) {
      buttonNew.value?.focus();
    }
  }
}
function focusNext() {
  const activeElement = document.activeElement;
  if (activeElement == null) {
    searchbar.value?.focus();
  } else {
    let nextElement = activeElement.nextElementSibling;
    while (nextElement != null) {
      if (
        nextElement instanceof HTMLButtonElement ||
        nextElement instanceof HTMLInputElement
      ) {
        return nextElement.focus();
      } else {
        nextElement = nextElement.nextElementSibling;
      }
    }

    if (nextElement == null) {
      searchbar.value?.focus();
    }
  }
}

// CONTEXT MENU
const contextMenuData = reactive({
  evaluationId: null as null | number,
  x: 0,
  y: 0
});
function openContextMenu(event: MouseEvent, evaluationId: number) {
  if (evaluationId == 0) return;
  contextMenuData.evaluationId = evaluationId;
  contextMenuData.x = event.clientX;
  contextMenuData.y = event.clientY;
}
</script>

<style lang="scss">
.evaluation-loading-menu {
  &__searchbar {
    width: calc(100% - 1rem);
    margin: 0.5rem;
    padding: 0.5rem;
    border: 1px solid var(--color-border);
    border-radius: 0.25rem;

    &:focus {
      outline: none;
      border-color: var(--color-primary);
    }
  }
}
</style>
